// ============================================================================
// Single source of truth for task assignee display label.
// Task-level: uses task.assigned_type. When per-checklist rows exist, those win.
// ============================================================================

import type { TaskChecklistAssignmentRow } from "@/lib/taskChecklistAssignmentRows";

export interface TaskForAssigneeLabel {
  assigned_type?: "owner" | "cleaner" | "unassigned" | "team" | null;
  cleaner_id?: string | null;
  assignee_user_id?: string | null;
  coordinator_user_id?: string | null;
}

export interface TaskAssigneeLabelMaps {
  /** Cleaner id -> display name. Fallback applied in util if null. */
  getCleanerName: (cleanerId: string | null) => string | null;
  /** User id (owner/team) -> display name. Use email prefix or "Owner"/"Team" if missing. */
  getAssigneeUserName: (userId: string | null) => string | null;
}

const FALLBACK_CLEANER = "Cleaner";
const FALLBACK_OWNER = "Owner";
const FALLBACK_TEAM_MEMBER = "Team Member";
const UNASSIGNED = "Unassigned";

/**
 * Returns assignee label from task using assigned_type as source of truth.
 * Never returns blank: uses fallbacks (Cleaner, Owner, Team, Unassigned).
 */
export function getTaskAssigneeLabel(
  task: TaskForAssigneeLabel,
  maps: TaskAssigneeLabelMaps
): string {
  const assignedType = task.assigned_type ?? "unassigned";

  switch (assignedType) {
    case "cleaner": {
      const cleanerId = task.cleaner_id ?? null;
      const raw = maps.getCleanerName(cleanerId)?.trim() || FALLBACK_CLEANER;
      const name = raw === FALLBACK_CLEANER ? raw : formatPersonLabel(raw) || raw;
      return `${name} (Cleaner)`;
    }
    case "owner": {
      const userId = task.assignee_user_id ?? null;
      const name = maps.getAssigneeUserName(userId)?.trim();
      if (!name || name === FALLBACK_OWNER) return "Self (Owner)";
      return `${name} (Owner)`;
    }
    case "team": {
      const userId = task.assignee_user_id ?? null;
      const name = maps.getAssigneeUserName(userId)?.trim() || FALLBACK_TEAM_MEMBER;
      return `${name} (Team Member)`;
    }
    default:
      return UNASSIGNED;
  }
}

/**
 * Optional coordinator suffix: " · Coordinator: Name".
 * Returns "" if no coordinator.
 */
export function getTaskAssigneeLabelWithCoordinator(
  task: TaskForAssigneeLabel,
  maps: TaskAssigneeLabelMaps
): string {
  const main = getTaskAssigneeLabel(task, maps);
  const coordId = task.coordinator_user_id ?? null;
  if (!coordId) return main;
  const coordName = maps.getAssigneeUserName(coordId)?.trim() || FALLBACK_TEAM_MEMBER;
  return `${main} · Coordinator: ${coordName}`;
}

/**
 * Safe display name from profile: full_name, else email prefix, else fallback.
 * Use for getAssigneeUserName so no blank assignee label.
 */
export function displayNameFromProfile(profile: {
  full_name?: string | null;
  email?: string | null;
} | null, fallback: string): string {
  if (!profile) return fallback;
  const name = (profile.full_name ?? "").trim();
  if (name) return formatPersonLabel(name);
  const email = (profile.email ?? "").trim();
  if (email) return email.split("@")[0] || fallback;
  return fallback;
}

/**
 * Disambiguate people for schedule/assign UI.
 * "Shane Mosoff" → "Shane M." · single-token names stay as-is.
 */
export function formatPersonLabel(fullName: string | null | undefined): string {
  const parts = (fullName ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0]!;
  const first = parts[0]!;
  const last = parts[parts.length - 1]!;
  const initial = last.charAt(0).toUpperCase();
  return initial ? `${first} ${initial}.` : first;
}

function isChecklistRowAssigned(
  row: Pick<TaskChecklistAssignmentRow, "assigned_type" | "cleaner_id" | "assignee_user_id">,
): boolean {
  if (!row.assigned_type || row.assigned_type === "unassigned") return false;
  if (row.assigned_type === "cleaner") return !!row.cleaner_id;
  if (row.assigned_type === "owner" || row.assigned_type === "team") return !!row.assignee_user_id;
  return false;
}

/** Whether a task has any assignee (task-level or per-checklist rows). */
export function isTaskAssignedForSchedule(
  task: TaskForAssigneeLabel,
  checklistRows: TaskChecklistAssignmentRow[] | undefined,
): boolean {
  if (checklistRows && checklistRows.length > 0) {
    return checklistRows.some(isChecklistRowAssigned);
  }
  const assignedType = task.assigned_type ?? "unassigned";
  if (assignedType === "unassigned") return false;
  if (assignedType === "cleaner") return !!task.cleaner_id;
  if (assignedType === "owner" || assignedType === "team") return !!task.assignee_user_id;
  return false;
}

/** Display label for schedule views; prefers per-checklist assignee names when present. */
export function getTaskAssigneeLabelForSchedule(
  task: TaskForAssigneeLabel,
  checklistRows: TaskChecklistAssignmentRow[] | undefined,
  maps: TaskAssigneeLabelMaps,
): string {
  if (checklistRows && checklistRows.length > 0) {
    const labels = [
      ...new Set(
        checklistRows
          .filter(isChecklistRowAssigned)
          .map((r) => r.assignee_label.trim())
          .filter((l) => l && l !== UNASSIGNED),
      ),
    ];
    if (labels.length === 0) return UNASSIGNED;
    if (labels.length === 1) return labels[0];
    return labels.join(" · ");
  }
  return getTaskAssigneeLabelWithCoordinator(task, maps);
}

/** Timeline badge copy, e.g. "Assigned to Karla Bautista". */
export function getTaskAssigneeBadgeLabel(assigneeLabel: string): string {
  if (assigneeLabel === UNASSIGNED) return UNASSIGNED;
  const primary = assigneeLabel.split(" · ")[0]?.replace(/ \((Cleaner|Owner|Team Member)\)$/, "") ?? assigneeLabel;
  return `Assigned to ${primary}`;
}
